import React, { useState } from 'react';
import { useCourseList } from '@/hooks/queries/course/useList';
import { useCourseDelete } from '@/hooks/mutations/course';
import { Course } from '@/types/courses';
import Loader from '@/components/Loader';
import { Button } from '@/components/ui/button';
import { BookOpen, ChevronRight, Plus, Edit2, Trash2 } from 'lucide-react';
import { cn } from '@/utils/styleUtils';
import { CourseModal } from './CourseModal';
import { AlertDialog } from '@/components/AlertDialog';
import { useToast } from '@/components/ui/use-toast';
import { getMediaUrl, getCourseTitle, getCourseImage } from '@/utils/common';
import { Pagination } from '@/components/Pagination';

interface CourseListProps {
  selectedCourseId: string;
  onSelectCourse: (id: string) => void;
}

export const CourseList = ({ selectedCourseId, onSelectCourse }: CourseListProps) => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const { data: coursesData, isLoading, refetch } = useCourseList({ pageNumber: page, pageSize: 20 });
  const { mutate: deleteCourse } = useCourseDelete();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [courseToEdit, setCourseToEdit] = useState<Course | null>(null);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<Course | null>(null);

  const courses: Course[] = coursesData?.data?.data || coursesData?.data || [];

  const handleOpenCreate = () => {
    setCourseToEdit(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (e: React.MouseEvent, course: Course) => {
    e.stopPropagation();
    setCourseToEdit(course);
    setIsModalOpen(true);
  };

  const handleOpenDelete = (e: React.MouseEvent, course: Course) => {
    e.stopPropagation();
    setCourseToDelete(course);
    setIsDeleteOpen(true);
  };

  const handleDelete = () => {
    if (!courseToDelete) return;
    deleteCourse(courseToDelete.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: 'Kurs o\'chirildi' });
        setIsDeleteOpen(false);
        setCourseToDelete(null);
        refetch();
      },
      onError: (err: any) => {
        toast({
          title: 'Xatolik',
          description: err?.response?.data?.message || 'Kursni o\'chirib bo\'lmadi',
          variant: 'destructive',
        });
      },
    });
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
      <div className="p-4 border-b border-border bg-slate-50/50 flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <BookOpen className="size-4 text-primary" />
            Kurslar
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Boshqaruv uchun kurs tanlang</p>
        </div>
        <Button
          size="sm"
          onClick={handleOpenCreate}
          className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1 text-xs h-8 px-2.5"
        >
          <Plus className="size-3.5" /> Yangi kurs
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
        {isLoading ? (
          <div className="py-10"><Loader /></div>
        ) : courses.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm">Kurslar topilmadi</div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-col gap-1.5">
            {courses.map((course) => {
              const isSelected = selectedCourseId === course.id;
              const title = getCourseTitle(course) || 'Nomsiz kurs';
              const imageSrc = getCourseImage(course);

              return (
                <div
                  key={course.id}
                  onClick={() => onSelectCourse(course.id)}
                  className={cn(
                    'w-full flex items-center justify-between p-2.5 rounded-xl cursor-pointer transition-all duration-200 group border',
                    isSelected
                      ? 'bg-primary text-white border-primary shadow-sm font-medium'
                      : 'hover:bg-slate-50 text-slate-700 border-transparent hover:border-slate-100'
                  )}
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className="size-10 rounded-lg bg-slate-100 overflow-hidden shrink-0 border border-slate-200/60 flex items-center justify-center">
                      {imageSrc ? (
                        <img
                          src={imageSrc}
                          alt={title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <BookOpen className="size-5 text-slate-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1 pr-1">
                      <p className="truncate text-xs font-semibold leading-tight">{title}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span
                          className={cn(
                            'text-[10px] px-1.5 py-0.2 rounded font-medium',
                            isSelected
                              ? 'bg-white/20 text-white'
                              : 'bg-blue-50 text-blue-700'
                          )}
                        >
                          {course.format || 'COURSE'}
                        </span>
                        {course.difficulty && (
                          <span
                            className={cn(
                              'text-[10px] opacity-75',
                              isSelected ? 'text-blue-100' : 'text-slate-400'
                            )}
                          >
                            • {course.difficulty}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={(e) => handleOpenEdit(e, course)}
                      className={cn(
                        'p-1 rounded-lg transition-colors opacity-0 group-hover:opacity-100',
                        isSelected ? 'hover:bg-white/20 text-white' : 'hover:bg-slate-200 text-slate-500'
                      )}
                      title="Tahrirlash"
                    >
                      <Edit2 className="size-3.5" />
                    </button>
                    <button
                      onClick={(e) => handleOpenDelete(e, course)}
                      className={cn(
                        'p-1 rounded-lg transition-colors opacity-0 group-hover:opacity-100',
                        isSelected ? 'hover:bg-white/20 text-white' : 'hover:bg-slate-200 text-red-500'
                      )}
                      title="O'chirish"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                    <ChevronRight
                      className={cn(
                        'size-4 shrink-0 transition-transform',
                        isSelected ? 'text-white' : 'text-slate-400'
                      )}
                    />
                  </div>
                </div>
              );
            })}
            </div>
            <Pagination className="mt-3" currentPage={page} setCurrentPage={setPage} paginationInfo={coursesData?.meta?.pagination ?? coursesData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      <CourseModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        courseToEdit={courseToEdit}
        onSuccess={() => refetch()}
      />

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Kursni o'chirishni tasdiqlaysizmi?"
        alertDescription="Ushbu kurs va unga tegishli ma'lumotlar o'chiriladi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};
